import React, { useState } from "react";
import { ChevronDown, ChevronUp, Dumbbell } from "lucide-react";

// --- Custom Circular Progressbar Component using SVG ---
const CustomCircularProgressbar = ({ value, text }) => {
  const radius = 35;
  const strokeWidth = 6;
  const normalizedRadius = radius - strokeWidth / 2;
  // Circumference = 2 * PI * R
  const circumference = normalizedRadius * 2 * Math.PI;

  // Ensure value is clamped between 0 and 100 for correct visual rendering
  const clampedValue = Math.max(0, Math.min(100, value));
  const strokeDashoffset = circumference - (clampedValue / 100) * circumference;

  let pathColorClass = "stroke-red-500"; // Default: < 50% (Red)
  if (clampedValue >= 100) {
    pathColorClass = "stroke-green-500"; // >= 100% (Green)
  } else if (clampedValue >= 50) {
    pathColorClass = "stroke-amber-500"; // >= 50% (Amber/Yellow)
  }

  return (
    <svg
      height={radius * 2}
      width={radius * 2}
      viewBox={`0 0 ${radius * 2} ${radius * 2}`}
      className="w-full h-full"
    >
      {/* Background/Trail Circle */}
      <circle
        className="stroke-gray-200"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {/* Progress Path */}
      <circle
        className={`transition-all duration-700 ease-out -rotate-90 origin-center ${pathColorClass}`}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        fill="none"
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {/* Text Label */}
      <text
        x="50%"
        y="50%"
        dy=".3em" // Center the text vertically
        textAnchor="middle"
        className="text-base font-semibold"
        fill="#374151" // gray-700
      >
        {text}
      </text>
    </svg>
  );
};

// --- Sample Data for the Preview has been removed. ---

const TrackedDisplay = ({ trackedData }) => {
  // Default prop removed here

  // Ensure trackedData is defined, otherwise use an empty structure to prevent crashes
  const data = trackedData || { sessions: [] };

  // Key is `${sessionIdx}-${groupIdx}`. null means all are closed. We open the
  // first group of the first session by default, if any session has groups.
  const firstSessionWithGroups = data.sessions.findIndex(
    (session) => session.groups.length > 0
  );
  const [openGroupKey, setOpenGroupKey] = useState(
    firstSessionWithGroups >= 0 ? `${firstSessionWithGroups}-0` : null
  );

  const toggleGroup = (key) => {
    setOpenGroupKey(openGroupKey === key ? null : key);
  };

  const getDifficultyColor = (difficulty) => {
    const normalized = (difficulty || "").trim().toLowerCase();

    // Use Tailwind's defined color classes for easier maintenance
    if (["beginner", "novice"].includes(normalized)) {
      return "text-green-600 font-semibold"; // Green
    } else if (normalized === "intermediate") {
      return "text-yellow-600 font-semibold"; // Yellow/Amber
    } else if (["advanced", "expert"].includes(normalized)) {
      return "text-red-600 font-semibold"; // Red/Error-Red
    } else {
      return "text-gray-500";
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4 p-4 md:p-6 bg-gray-50 min-h-screen font-sans">
      <h1 className="text-3xl font-extrabold text-gray-800 text-center mb-6">
        Workout Progression Tracker
      </h1>

      <div className="space-y-8">
        {data.sessions.length === 0 ? (
          <div className="text-center p-10 bg-white rounded-xl shadow-lg text-gray-600">
            No workout data available to track.
          </div>
        ) : (
          data.sessions.map((session, sIdxOuter) => (
            <div key={session.planTrackingId || sIdxOuter} className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-1">
                Plan: {session.planTitle || "Unknown source"}
              </h2>

              {session.groups.map((group, gIdx) => {
                const groupKey = `${sIdxOuter}-${gIdx}`;
                const isGroupOpen = openGroupKey === groupKey;
                return (
                  <div
                    key={groupKey}
                    className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300"
                  >
                    {/* ACCORDION HEADER - Clickable area */}
                    <div
                      className={`p-4 cursor-pointer flex justify-between items-center transition duration-200
                                    ${
                                      isGroupOpen
                                        ? "bg-orange-primary text-white shadow-md"
                                        : "bg-orange-50 hover:bg-orange-100"
                                    }`}
                      onClick={() => toggleGroup(groupKey)}
                    >
                      <div className="flex items-center gap-3">
                        <Dumbbell
                          className={`w-6 h-6 ${
                            isGroupOpen ? "text-white" : "text-orange-600"
                          }`}
                        />
                        <h3
                          className={`text-lg font-bold ${
                            isGroupOpen ? "text-white" : "text-orange-800"
                          }`}
                        >
                          {group.group_name.toUpperCase()}
                        </h3>
                      </div>

                      {/* Accordion Icon */}
                      {isGroupOpen ? (
                        <ChevronUp className="w-5 h-5 text-white transform transition duration-300" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-orange-600 transform transition duration-300" />
                      )}
                    </div>

                    {/* ACCORDION BODY - Conditionally Rendered Content */}
                    {isGroupOpen && (
                      <div className="p-4 md:p-6 animate-fadeIn">
                        <div className="mt-2 space-y-6">
                          {group.exercises.map((exercise, eIdx) => {
                            // A rep target of 0 means this exercise is tracked
                            // by hold/duration rather than rep count, so the
                            // sets table shows Time instead of Reps for it.
                            const isTimeBased =
                              exercise.sets.length > 0 &&
                              exercise.sets.every((set) => !set.target_reps);
                            return (
                            <div
                              key={eIdx}
                              className="bg-gray-100 p-4 rounded-lg shadow-inner flex flex-col lg:flex-row items-start lg:items-center gap-6 border-l-4 border-orange-400"
                            >
                              {/* Progress Circle & Info Block */}
                              <div className="flex flex-col items-center w-full lg:w-1/4">
                                {/* Progress Circle - Using the new Custom component */}
                                <div className="w-24 h-24 flex-shrink-0 mb-4">
                                  <CustomCircularProgressbar
                                    value={exercise.progress_percentage}
                                    text={`${exercise.progress_percentage}%`}
                                  />
                                </div>

                                {/* Exercise Info */}
                                <div className="text-center lg:text-left text-sm text-gray-700 w-full px-2">
                                  <h4 className="text-lg font-bold text-gray-800 mb-1">
                                    {exercise.exercise_name}
                                  </h4>
                                  <p
                                    className={`text-xs ${getDifficultyColor(
                                      exercise.difficulty
                                    )}`}
                                  >
                                    Difficulty: {exercise.difficulty || "Unknown"}
                                  </p>
                                  <p className="text-xs">
                                    Target Muscle: {exercise.target_muscle || "N/A"}
                                  </p>
                                  <p className="text-xs">
                                    Rest After: {exercise.rest_time_after}s
                                  </p>
                                </div>
                              </div>

                              {/* Sets Table */}
                              <div className="overflow-x-auto w-full lg:w-3/4">
                                <table className="min-w-full text-xs text-gray-700 rounded-lg overflow-hidden">
                                  <thead className="bg-orange-200">
                                    <tr>
                                      <th className="p-2 text-left">Set</th>
                                      <th className="p-2 text-center">
                                        {isTimeBased ? "Time (T/A)" : "Reps (T/A)"}
                                      </th>
                                      <th className="p-2 text-center">
                                        Weight (T/A)
                                      </th>
                                      <th className="p-2 text-center">
                                        Rest (T/A)
                                      </th>
                                      <th className="p-2 text-center">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {exercise.sets.map((set, setIdx) => {
                                      const isComplete = set.is_completed;
                                      return (
                                        <tr
                                          key={setIdx}
                                          className={`text-center transition-colors duration-150 ${
                                            isComplete
                                              ? "bg-green-50 hover:bg-green-100"
                                              : "bg-red-50 hover:bg-red-100"
                                          } border-b border-gray-200 last:border-b-0`}
                                        >
                                          <td className="p-2 text-left font-medium text-gray-800">
                                            {setIdx + 1}
                                          </td>
                                          <td className="p-2">
                                            {isTimeBased ? (
                                              <>
                                                {set.target_time_seconds}s /{" "}
                                                {set.actual_time_seconds || "-"}s
                                              </>
                                            ) : (
                                              <>
                                                {set.target_reps} /{" "}
                                                {set.actual_reps || "-"}
                                              </>
                                            )}
                                          </td>
                                          <td className="p-2">
                                            {set.target_weight} /{" "}
                                            {set.actual_weight || "-"}
                                          </td>
                                          <td className="p-2">
                                            {set.target_rest_seconds}s /{" "}
                                            {set.actual_rest_seconds || "-"}s
                                          </td>
                                          <td className="p-2">
                                            <span
                                              className={`inline-block px-3 py-1 rounded-full text-[10px] font-semibold tracking-wide shadow-sm
                                                                                    ${
                                                                                      isComplete
                                                                                        ? "bg-green text-white" // Using custom class .bg-green
                                                                                        : "bg-error-red text-white" // Using custom class .bg-error-red
                                                                                    }`}
                                            >
                                              {isComplete
                                                ? "COMPLETED"
                                                : "INCOMPLETE"}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TrackedDisplay;
